/**
 * The pipeline board (docs/ux/pipeline.md): a status strip that filters one
 * dense table, five early-warning tiles, the filter bar, the results line and
 * the legend. Red comes only from the server's SLA fields. Below 768 px the
 * table gives way to cards and the filters to a sheet.
 */

import { statusLabel, type Status } from '@beam/core/status';
import type { SortingState } from '@tanstack/react-table';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useSession } from '@/app/session';
import type { MatterRow, PipelineSummary } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { DaysCell } from '@/components/matter/DaysCell';
import { FlagMenu } from '@/components/matter/FlagMenu';
import { FlagText, activeFlags } from '@/components/matter/FlagText';
import { StatusChip } from '@/components/matter/StatusChip';
import { FilterBar } from '@/components/shell/FilterBar';
import { Legend, LegendItem } from '@/components/shell/Legend';
import { MatterCard, MatterCardList } from '@/components/shell/MatterCard';
import { PageHeader } from '@/components/shell/PageHeader';
import { QueueTable } from '@/components/shell/QueueTable';
import { QueueTabs } from '@/components/shell/QueueTabs';
import { Tile, TileRow } from '@/components/shell/Tile';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { useShellWidth } from '@/components/shell/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { reportTypeLabel } from '@beam/core/status';
import { DueCell, gateSubState, pipelineColumns } from './columns';
import { NewCaseDialog } from '../intake/NewCaseDialog';
import { ExportCsvButton } from './ExportCsvButton';
import { StatusStrip } from './StatusStrip';
import { deriveCounts, usePipelineRows, usePipelineSummary, useMetrics } from './usePipelineData';
import { MetricsTab } from './MetricsTab';
import { activeCount, applyFilters, usePipelineFilters, type Filters } from './usePipelineFilters';
import { DEFAULT_ORDER_WORDS, DEFAULT_THRESHOLDS, PIPELINE_VIEWS, SORT_HINT, TILES, type SortKey, type TileId, type ViewId } from './views';

const TABS = [{ value: 'board', label: 'Board' }, { value: 'metrics', label: 'Metrics' }];

export function PipelinePage() {
  useShellWidth('wide');
  const { filters, statuses, toggleStatus, toggleView, toggleTile, clearStatuses, setTab, setSort, update, clear } = usePipelineFilters();
  const rows = usePipelineRows();
  const summary = usePipelineSummary();
  const metrics = useMetrics(filters.tab === 'metrics');
  const phone = useIsPhone();
  const { me } = useSession();

  const allRows = rows.data ?? [];
  const finance = filters.view === 'finance';
  const visible = useMemo(() => applyFilters(allRows, filters), [allRows, filters]);
  const deferredCount = useDeferredValue(visible.length);
  const thresholds = summary.data?.thresholds ?? DEFAULT_THRESHOLDS;
  const counts = summary.data?.byStatus ?? (rows.data ? deriveCounts(rows.data) : null);
  const total = summary.data?.total ?? rows.data?.length ?? null;
  const overdueTotal = summary.data?.overdueTotal ?? (rows.data ? rows.data.filter((r) => r.slaState === 'overdue').length : null);
  const columns = useMemo(() => pipelineColumns(finance), [finance]);
  const sorting: SortingState = filters.sort ? [{ id: filters.sort, desc: filters.dir === 'desc' }] : [];
  const slice = filters.view ?? (filters.status !== null ? `status-${filters.status}` : 'all');
  const doctors = useMemo(() => uniqueBy(allRows.map((r) => r.doctor), (d) => d.slug), [allRows]);
  const referrers = useMemo(() => uniqueBy(allRows.map((r) => r.referrer), (r) => r.id), [allRows]);

  // The URL holds the trimmed query; the typed text lives here so a trailing space survives the round trip instead of being swallowed.
  const [typed, setTyped] = useState(filters.q);
  useEffect(() => { if (typed.trim() !== filters.q) setTyped(filters.q); }, [filters.q]); // eslint-disable-line react-hooks/exhaustive-deps

  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState<Filters>(filters);
  function openSheet(open: boolean) {
    if (open) setDraft(filters);
    setSheetOpen(open);
  }

  const [newCase, setNewCase] = useState(false);

  const header = (
    <PageHeader
      title="Pipeline"
      actions={
        <>
          <QueueTabs tabs={TABS} value={filters.tab} onChange={(v) => setTab(v as Filters['tab'])} label="Pipeline tabs" />
          {filters.tab === 'board' ? <ExportCsvButton rows={visible} slice={slice} className="h-11 md:h-9" /> : null}
          {/* A referral arrives while the admin is on the board, so the case starts here (docs/ux/intake.md). */}
          {filters.tab === 'board' ? <Button className="h-11 md:h-9" onClick={() => setNewCase(true)}>New case</Button> : null}
        </>
      }
    />
  );

  const dialog = <NewCaseDialog open={newCase} onOpenChange={setNewCase} />;

  if (filters.tab === 'metrics') {
    return (
      <>
        {header}
        {dialog}
        <MetricsTab metrics={metrics.data} isPending={metrics.isPending} isError={metrics.isError} onRetry={() => void metrics.refetch()} />
      </>
    );
  }

  // The phone carries the hint only when a sort is chosen: the filter bar's hint cannot shrink beside the Filters button, the default order sentence is long, and the results line sits under the cards.
  const hint = phone && !filters.sort
    ? ''
    : filters.sort
      ? <>Order: {SORT_HINT[filters.sort][filters.dir]}. <Button variant="link" className="h-auto p-0 text-xs" onClick={() => setSort(null)}>Reset</Button></>
      : `Order: ${DEFAULT_ORDER_WORDS}`;

  const emptyCopy = emptyMessage(filters, allRows.length);

  return (
    // 12 px between blocks: the density budget in docs/ux/pipeline.md leaves
    // ten 46 px rows above the fold at 1536x864 only if the chrome stays at
    // about 383 px.
    <div className="flex flex-col gap-3">
      {header}
      {dialog}
      <StatusStrip
        counts={counts}
        total={total}
        overdueTotal={overdueTotal}
        loading={rows.isPending && summary.isPending}
        statuses={statuses}
        view={filters.view}
        onToggleStatus={toggleStatus}
        onToggleView={toggleView}
        onClear={clearStatuses}
      />
      <div className="flex flex-col gap-1">
        <TileRow label="Early warnings">
          {TILES.filter((t) => !phone || t.id === 'overdue' || t.id === 'paperwork').map((t) => (
            <Tile
              key={t.id}
              value={summary.data ? tileValue(summary.data, t.id) : null}
              label={t.label(thresholds)}
              tone={t.tone}
              pressed={filters.tile === t.id}
              onPress={() => toggleTile(t.id)}
              description={t.description(thresholds)}
              loading={summary.isPending}
              unavailable={summary.isError}
            />
          ))}
          {phone ? null : (
            <Tile
              value={summary.data?.tiles.awaitingInformation}
              label="awaiting information"
              tone="neutral"
              pressed={filters.view === 'awaiting-information'}
              onPress={() => toggleView('awaiting-information')}
              description="Cases parked at 13 Awaiting Information."
              loading={summary.isPending}
              unavailable={summary.isError}
            />
          )}
        </TileRow>
        {summary.isError ? (
          <p className="text-xs text-beam-text-muted">
            Counts unavailable. <Button variant="link" className="h-auto p-0 text-xs" onClick={() => void summary.refetch()}>Retry</Button>
          </p>
        ) : null}
      </div>

      <FilterBar
        activeCount={activeCount(filters)}
        hint={hint}
        sheetOpen={sheetOpen}
        onSheetOpenChange={openSheet}
        onApply={() => { update({ ...draft }); setSheetOpen(false); }}
        onClear={() => { clear(); setSheetOpen(false); }}
        desktop={
          <>
            <Input
              type="search"
              value={typed}
              onChange={(e) => { setTyped(e.target.value); update({ q: e.target.value }); }}
              placeholder="Search case or patient"
              aria-label="Search this queue"
              className="h-9 w-60 bg-beam-paper"
            />
            <Select value={filters.doctor ?? 'all'} onValueChange={(v) => update({ doctor: v === 'all' ? null : v })}>
              <SelectTrigger aria-label="Doctor" className="h-9 min-w-40 bg-beam-paper"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Doctor: all</SelectItem>
                {doctors.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.referrer ?? 'all'} onValueChange={(v) => update({ referrer: v === 'all' ? null : v })}>
              <SelectTrigger aria-label="Referrer" className="h-9 min-w-40 bg-beam-paper"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Referrer: all</SelectItem>
                {referrers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {activeCount(filters) > 0 ? <Button variant="link" className="h-9 px-2" onClick={clear}>Clear</Button> : null}
          </>
        }
        sheet={<FilterSheetBody draft={draft} setDraft={setDraft} counts={counts} total={total} summary={summary.data ?? null} thresholds={thresholds} doctors={doctors} referrers={referrers} />}
      />

      {rows.isError ? (
        <ErrorPanel message="The pipeline could not be loaded." onRetry={() => void rows.refetch()} />
      ) : phone ? (
        <MatterCardList state={rows.isPending ? 'loading' : 'ready'}>
          {!rows.isPending && visible.length === 0 ? <EmptyState title={emptyCopy} action={activeCount(filters) > 0 ? <Button variant="link" onClick={clear}>Clear filters</Button> : undefined} /> : null}
          {visible.map((r) => <PipelineCard key={r.ref} row={r} finance={finance} />)}
        </MatterCardList>
      ) : (
        <QueueTable
          columns={columns}
          data={visible}
          sorting={sorting}
          onSortingChange={(updater) => {
            const next = typeof updater === 'function' ? updater(sorting) : updater;
            const first = next[0];
            setSort(first ? (first.id as SortKey) : null, first?.desc ? 'desc' : 'asc');
          }}
          getRowId={(r) => r.ref}
          rowHref={(r) => `/matters/${r.ref}`}
          rowState={(r) => (r.slaState === 'overdue' ? 'overdue' : 'default')}
          caption={`Cases in the pipeline, ${visible.length} of ${allRows.length}, ordered by ${filters.sort ? SORT_HINT[filters.sort][filters.dir] : DEFAULT_ORDER_WORDS}`}
          state={rows.isPending ? 'loading' : 'ready'}
          empty={<EmptyState className="border-0 py-6" title={emptyCopy} action={activeCount(filters) > 0 ? <Button variant="link" onClick={clear}>Clear filters</Button> : undefined} />}
        />
      )}

      <p aria-live="polite" className="tabular text-xs text-beam-text-muted">
        {rows.data ? `Showing ${deferredCount} of ${allRows.length} cases` : ''}
      </p>

      <Legend>
        <LegendItem term={<span className="flex gap-1"><StatusChip status={7} variant="number" /><StatusChip status={11} variant="number" /></span>}>1 to 9 and 11: in flow</LegendItem>
        <LegendItem term={<span className="flex gap-1"><StatusChip status={10} variant="number" /><StatusChip status={12} variant="number" /></span>}>done</LegendItem>
        <LegendItem term={<StatusChip status={13} size="sm" />}>parked, waiting on information</LegendItem>
        <LegendItem term={<StatusChip status={14} size="sm" />}>did not attend</LegendItem>
        <LegendItem term={<StatusChip status={15} size="sm" />}>cancelled, terminal</LegendItem>
        <LegendItem term={<span className="font-medium">Days</span>}>days in the current status; red past the allowed days for that status, amber within a day of them</LegendItem>
        {thresholds && summary.data?.thresholds.placeholder ? <LegendItem term={<span className="font-medium">Allowances</span>}>from sla.json; the current values are placeholders until Beam confirms them</LegendItem> : null}
        <LegendItem term={<span className="tabular font-semibold text-beam-error">Due</span>}>in red: the due date has passed</LegendItem>
        <LegendItem term={<FlagText kind="urgent" />}>set by admin</LegendItem>
        <LegendItem term={<span className="flex gap-2"><FlagText kind="repeatDna" /><FlagText kind="lateCancellation" /></span>}>set by the engine when the transition happened</LegendItem>
        {me?.persona === 'doctor' ? <LegendItem term={<span className="font-medium">Doctors</span>}>your reports are on the Doctors tab; this board shows everything</LegendItem> : null}
      </Legend>
    </div>
  );
}

function tileValue(summary: PipelineSummary, id: TileId): number {
  switch (id) {
    case 'overdue': return summary.tiles.overdue;
    case 'paperwork': return summary.tiles.paperworkNotIn;
    case 'drafts': return summary.tiles.draftsDueNotStarted;
    case 'unconfirmed': return summary.tiles.appointmentsUnconfirmed;
    default: return 0;
  }
}

function emptyMessage(f: Filters, total: number): string {
  if (total === 0) return 'No cases in the pipeline.';
  if (f.q.trim() || f.doctor || f.referrer) return 'No cases match these filters.';
  if (f.tile) return TILES.find((t) => t.id === f.tile)!.empty;
  if (f.view) return `No cases in ${PIPELINE_VIEWS.find((v) => v.id === f.view)!.label}.`;
  if (f.status !== null) return `No cases at ${f.status} ${statusLabel(f.status)}.`;
  return 'No cases match these filters.';
}

function uniqueBy<T>(items: T[], key: (t: T) => string): T[] {
  const seen = new Map<string, T>();
  for (const i of items) if (!seen.has(key(i))) seen.set(key(i), i);
  return [...seen.values()].sort((a, b) => key(a).localeCompare(key(b)));
}

function PipelineCard({ row, finance }: { row: MatterRow; finance: boolean }) {
  const flags = activeFlags(row.flags);
  const pairs = finance
    ? [{ label: 'Doctor', value: row.doctor.name }, { label: 'Referrer', value: row.referrer.name }, { label: 'Billing', value: row.billingBand ?? 'Not set' }]
    : [{ label: 'Doctor', value: row.doctor.name }, { label: 'Referrer', value: row.referrer.name }, { label: 'Type', value: reportTypeLabel(row.reportType) }];
  return (
    <MatterCard
      href={`/matters/${row.ref}`}
      reference={row.ref}
      patient={row.patientAsOnLoi}
      chip={<StatusChip status={row.status} size="md" subState={finance ? gateSubState(row) : undefined} />}
      due={<DueCell dueDate={row.dueDate} />}
      days={<DaysCell days={row.daysWaiting} sla={row.slaState} allowedDays={row.allowedDays} status={row.status} />}
      pairs={pairs}
      flags={<FlagMenu matterRef={row.ref} flags={row.flags} max={flags.length} layout="inline" />}
      overdue={row.slaState === 'overdue'}
    />
  );
}

function FilterSheetBody({ draft, setDraft, counts, total, summary, thresholds, doctors, referrers }: {
  draft: Filters;
  setDraft: (f: Filters) => void;
  counts: PipelineSummary['byStatus'] | null;
  total: number | null;
  summary: PipelineSummary | null;
  thresholds: typeof DEFAULT_THRESHOLDS;
  doctors: Array<{ slug: string; name: string }>;
  referrers: Array<{ id: string; name: string }>;
}) {
  const viewValue = draft.view ?? (draft.status !== null ? `status:${draft.status}` : 'all');
  const rowClass = 'flex min-h-11 items-center justify-between gap-3';
  return (
    <div className="flex flex-col gap-4 pb-2">
      <fieldset>
        <legend className="eyebrow mb-1 text-beam-text-muted">View</legend>
        <RadioGroup value={viewValue} onValueChange={(v) => setDraft({ ...draft, view: v === 'all' || v.startsWith('status:') ? null : (v as ViewId), status: v.startsWith('status:') ? (Number(v.slice(7)) as Status) : null })}>
          <Label className={rowClass}><span className="flex items-center gap-3"><RadioGroupItem value="all" />All</span><span className="tabular text-beam-text-muted">{total ?? ''}</span></Label>
          {PIPELINE_VIEWS.map((v) => (
            <Label key={v.id} className={rowClass}>
              <span className="flex items-center gap-3"><RadioGroupItem value={v.id} />{v.label}</span>
              <span className="tabular text-beam-text-muted">{counts ? v.statuses.reduce((n, s) => n + (counts[String(s)]?.count ?? 0), 0) : ''}</span>
            </Label>
          ))}
          {draft.status !== null && !PIPELINE_VIEWS.some((v) => v.statuses.length === 1 && v.statuses[0] === draft.status) ? (
            <Label className={rowClass}><span className="flex items-center gap-3"><RadioGroupItem value={`status:${draft.status}`} />{draft.status} {statusLabel(draft.status)}</span></Label>
          ) : null}
        </RadioGroup>
      </fieldset>
      <fieldset>
        <legend className="eyebrow mb-1 text-beam-text-muted">Warnings</legend>
        <RadioGroup value={draft.tile ?? 'none'} onValueChange={(v) => setDraft({ ...draft, tile: v === 'none' ? null : (v as TileId) })}>
          <Label className={rowClass}><span className="flex items-center gap-3"><RadioGroupItem value="none" />None</span></Label>
          {TILES.map((t) => (
            <Label key={t.id} className={rowClass}>
              <span className="flex items-center gap-3"><RadioGroupItem value={t.id} />{capitalise(t.label(thresholds).split(',')[0]!)}</span>
              <span className="tabular text-beam-text-muted">{summary ? tileValue(summary, t.id) : ''}</span>
            </Label>
          ))}
        </RadioGroup>
      </fieldset>
      <div className="flex flex-col gap-2">
        <Label htmlFor="sheet-q" className="eyebrow text-beam-text-muted">Search</Label>
        <Input id="sheet-q" type="search" value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} placeholder="Case or patient" className="h-11 text-base" />
      </div>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="sheet-doctor">Doctor</Label>
        <Select value={draft.doctor ?? 'all'} onValueChange={(v) => setDraft({ ...draft, doctor: v === 'all' ? null : v })}>
          <SelectTrigger id="sheet-doctor" className="h-11 w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {doctors.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="sheet-referrer">Referrer</Label>
        <Select value={draft.referrer ?? 'all'} onValueChange={(v) => setDraft({ ...draft, referrer: v === 'all' ? null : v })}>
          <SelectTrigger id="sheet-referrer" className="h-11 w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {referrers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
